/**
 * The e621 family's strip (e621, e926, e6AI): Hot, and the best posts of the day, week, month or year.
 *
 * The site offers these in its own menu. "Hot" is its `order:rank`; "best of" is a score order inside a date window, which is
 * `order:score date:<window>`. All five were checked live against e926 on 2026-10-25. They are sent as one parameter, not mixed
 * into `tags`, because `tags` is shared by every source in a multi-source search and `order:rank` means nothing to the others.
 */
export const E621_FAMILY: readonly string[] = ["e621", "e926", "e6ai"]

const E621_NAMES: Record<string, string> = { e621: "e621", e926: "e926", e6ai: "e6AI" }

/** The strip's heading: the family members that are switched on ("e6AI", or "e621 / e926"). */
export function e621StripLabel(sites: readonly string[]): string {
  return E621_FAMILY.filter((id) => sites.includes(id)).map((id) => E621_NAMES[id]).join(" / ")
}

export const E621_ORDERS = [
  { value: "hot", label: "Hot", tokens: ["order:rank"] },
  { value: "day", label: "Best of today", tokens: ["order:score", "date:day"] },
  { value: "week", label: "Best of the week", tokens: ["order:score", "date:week"] },
  { value: "month", label: "Best of the month", tokens: ["order:score", "date:month"] },
  { value: "year", label: "Best of the year", tokens: ["order:score", "date:year"] },
] as const

export type E621Order = (typeof E621_ORDERS)[number]["value"]

export function isE621Order(value: unknown): value is E621Order {
  return typeof value === "string" && E621_ORDERS.some((item) => item.value === value)
}

/** `tags` for one source: unchanged for every source outside the family (and when no order is chosen), otherwise the order's terms
 * are added. A search that already carries its own order or date term keeps it (the viewer asked for that). */
export function withE621Order(site: string, tags: string, order: unknown): string {
  if (!E621_FAMILY.includes(site) || !isE621Order(order)) return tags
  if (/(^|[\s,])(order|date):/i.test(tags)) return tags
  const tokens = E621_ORDERS.find((item) => item.value === order)!.tokens
  return [tags.trim(), ...tokens].filter(Boolean).join(" ")
}
